/**
 * TableCard - one auto-mapped table: header with detected time/entity/rows,
 * an expandable per-column editor (include / type / time / entity), and the
 * interactive ImportScope control. Reused by the multi-table picker and the
 * single-table sync editor.
 */

import React, { useState } from 'react';
import {
  Box, Paper, Typography, Stack, Chip, MenuItem, TextField, Collapse,
  Divider, Tooltip, Checkbox, Button, IconButton, useTheme, alpha,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import TableRowsIcon from '@mui/icons-material/TableRows';
import BoltIcon from '@mui/icons-material/Bolt';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { TYPE_OPTIONS, tableEstSignals, tableSignalCols, compactNum } from './connectorMeta';
import ImportScope from './ImportScope';

const EVENT_TOOLTIP = 'Mark this column as an event. Each non-empty cell is sent to '
  + 'the Events Engine and scored (sentiment + relevance/importance/impact) instead '
  + 'of being stored as a signal time-series. Cannot be the time or id column.';

/**
 * Field name with a pencil edit: the friendly display name shown across the
 * whole platform (signals page, charts, segments, trainings). The underlying
 * source field / signal key is never changed — e.g. rename "o" to "Open".
 */
function ColumnName({ column: c, onRename }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  const startEdit = () => { setDraft(c.display_name || ''); setEditing(true); };
  const commit = () => {
    const v = draft.trim();
    onRename(v && v !== c.source_field ? v : '');
    setEditing(false);
  };

  if (editing) {
    return (
      <TextField
        size="small" autoFocus fullWidth value={draft}
        placeholder={c.source_field}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') setEditing(false);
        }}
        inputProps={{ style: { fontSize: 13, paddingTop: 4, paddingBottom: 4 } }}
      />
    );
  }

  return (
    <Stack direction="row" alignItems="center" spacing={0.25} sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 13, minWidth: 0 }} noWrap
        title={c.display_name ? `${c.display_name} (from ${c.source_field})` : c.source_field}>
        {c.display_name || c.source_field}
        {c.display_name && (
          <Typography component="span" sx={{ fontSize: 11, color: 'text.secondary', ml: 0.5 }}>
            ({c.source_field})
          </Typography>
        )}
      </Typography>
      <Tooltip title="Rename — used as the display name everywhere (charts, segments, trainings)">
        <IconButton size="small" onClick={startEdit} sx={{ p: 0.25, color: 'text.secondary' }}>
          <EditOutlinedIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}

export default function TableCard({
  table: t, selectable = true, selected, onToggleSelect,
  expanded, onToggleExpand, updateColumn,
  scope, setScope, showScope = true,
  // Optional extensions used by the dataset review flow so a file renders as
  // a single card: extra header chips, a right-side action (e.g. an import
  // switch), content injected at the top of the expanded editor, and a
  // dimmed state for files that won't be imported.
  extraChips = null, headerAction = null, detailsTop = null, dimmed = false,
}) {
  const theme = useTheme();
  const card = { border: `1px solid ${theme.palette.divider}`, borderRadius: 2, bgcolor: theme.palette.background.paper };
  const active = (selectable ? selected : true) && !dimmed;

  return (
    <Paper elevation={0} sx={{
      ...card, overflow: 'hidden', opacity: dimmed ? 0.6 : 1,
      borderColor: active ? alpha(theme.palette.primary.main, 0.5) : theme.palette.divider,
    }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ p: 1.25 }}>
        {selectable && (
          <Checkbox size="small" checked={!!selected} onChange={(e) => onToggleSelect?.(e.target.checked)} />
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 14 }} noWrap>{t.table_name}</Typography>
          <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
            <Chip size="small" variant="outlined" label={`${t.columns.length} cols`} sx={{ height: 20, fontSize: 11 }} />
            {t.row_estimate != null && (
              <Tooltip title={`~${tableEstSignals(t)?.toLocaleString()} signals if fully imported`}>
                <Chip size="small" variant="outlined" icon={<TableRowsIcon sx={{ fontSize: 13 }} />} label={`~${compactNum(t.row_estimate)} rows`} sx={{ height: 20, fontSize: 11 }} />
              </Tooltip>
            )}
            {t.timestamp_column
              ? <Chip size="small" color="primary" variant="outlined" icon={<AccessTimeIcon sx={{ fontSize: 13 }} />} label={t.timestamp_column} sx={{ height: 20, fontSize: 11 }} />
              : (!dimmed && (
                <Tooltip title="A time column is required — open Configure and tap 'time' on the timestamp column">
                  <Chip size="small" color="warning" variant="outlined" icon={<AccessTimeIcon sx={{ fontSize: 13 }} />} label="Pick a time column" sx={{ height: 20, fontSize: 11 }} />
                </Tooltip>
              ))}
            {t.entity_column && <Chip size="small" variant="outlined" icon={<FingerprintIcon sx={{ fontSize: 13 }} />} label={t.entity_column} sx={{ height: 20, fontSize: 11 }} />}
            {t.columns.some((c) => c.is_event) && (
              <Tooltip title="Columns published to the Events Engine">
                <Chip size="small" variant="outlined" color="warning" icon={<BoltIcon sx={{ fontSize: 13 }} />}
                  label={`${t.columns.filter((c) => c.is_event).length} event${t.columns.filter((c) => c.is_event).length === 1 ? '' : 's'}`}
                  sx={{ height: 20, fontSize: 11 }} />
              </Tooltip>
            )}
            {t.watermark_column && (
              <Tooltip title="Incremental sync — only new rows after the first run">
                <Chip size="small" variant="outlined" color="success" label="incremental" sx={{ height: 20, fontSize: 11 }} />
              </Tooltip>
            )}
            {extraChips}
          </Stack>
        </Box>
        {onToggleExpand && (
          <Button size="small" onClick={() => onToggleExpand?.()}
            endIcon={<ExpandMoreIcon sx={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />}
            sx={{ textTransform: 'none', color: 'text.secondary' }}>
            Configure
          </Button>
        )}
        {headerAction}
      </Stack>

      <Collapse in={expanded}>
        <Divider />
        <Box sx={{ p: 1.25 }}>
          {detailsTop}
          <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
            {t.columns.map((c, idx) => (
              <Stack key={c.source_field + idx} direction="row" spacing={1} alignItems="center"
                sx={{ py: 0.4, opacity: c.included || c.is_timestamp || c.is_entity || c.is_event ? 1 : 0.5 }}>
                <Checkbox size="small" checked={c.included} onChange={(e) => updateColumn(idx, { included: e.target.checked })} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <ColumnName column={c} onRename={(name) => updateColumn(idx, { display_name: name || undefined })} />
                  {Array.isArray(c.sample_values) && c.sample_values.length > 0 && (
                    <Typography noWrap title={c.sample_values.join('  ·  ')}
                      sx={{ fontSize: 11, fontFamily: 'monospace', color: 'text.secondary', opacity: 0.9 }}>
                      {c.sample_values.join('  ·  ')}
                      {c.unit ? (
                        <Typography component="span" sx={{ ml: 0.5, fontStyle: 'italic', opacity: 0.8 }}>
                          {c.unit}
                        </Typography>
                      ) : null}
                    </Typography>
                  )}
                </Box>
                <TextField select size="small" value={c.data_type} disabled={c.is_event} onChange={(e) => updateColumn(idx, { data_type: e.target.value })} sx={{ width: 130 }}>
                  {TYPE_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value} sx={{ fontSize: 13 }}>{o.label}</MenuItem>)}
                </TextField>
                <Tooltip title="Time column">
                  <Chip size="small" clickable label="time" color={c.is_timestamp ? 'primary' : 'default'} variant={c.is_timestamp ? 'filled' : 'outlined'}
                    onClick={() => updateColumn(idx, { is_timestamp: !c.is_timestamp, is_entity: false, is_event: false })} sx={{ height: 22, fontSize: 11 }} />
                </Tooltip>
                <Tooltip title="Entity / identity (panel data)">
                  <Chip size="small" clickable label="id" color={c.is_entity ? 'secondary' : 'default'} variant={c.is_entity ? 'filled' : 'outlined'}
                    onClick={() => updateColumn(idx, { is_entity: !c.is_entity, is_timestamp: false, is_event: false })} sx={{ height: 22, fontSize: 11 }} />
                </Tooltip>
                <Tooltip title={EVENT_TOOLTIP}>
                  <Chip size="small" clickable label="event" color={c.is_event ? 'warning' : 'default'} variant={c.is_event ? 'filled' : 'outlined'}
                    onClick={() => updateColumn(idx, c.is_event
                      ? { is_event: false }
                      : { is_event: true, is_timestamp: false, is_entity: false, included: true })}
                    sx={{ height: 22, fontSize: 11 }} />
                </Tooltip>
              </Stack>
            ))}
          </Box>

          {showScope && setScope && (
            <ImportScope
              scope={scope} onChange={setScope}
              rowEstimate={t.row_estimate} hasTimestamp={!!t.timestamp_column}
              signalCols={tableSignalCols(t)}
            />
          )}
        </Box>
      </Collapse>
    </Paper>
  );
}
